import {
  SideNavLinkProps as CoreSideNavLinkProps,
  SideNavLink as CoreSideNavLink,
} from "./internal/SideNavLink";
import { Ref, forwardRef, useCallback } from "react";
import { useSideNavContext } from "./internal/SideNavContext";

/**
 * Props for SideNavLink with routing support
 * @property {string} [href] - URL for the link (used when pathname is not provided)
 * @property {string} pathname - Pathname for client-side routing
 * @property {string} [search] - Optional search parameters for the URL
 * @property {boolean} [active] - When true, marks the link as the current active page
 * @property {boolean} [disabled] - When true, disables the link and prevents interaction
 * @extends CoreSideNavLinkProps
 */
type SideNavLinkWithRoutingProps = CoreSideNavLinkProps & {
  /**
   * Pathname for client-side routing
   */
  pathname: string;
  /**
   * Optional search parameters for the URL
   */
  search?: string;
};

/**
 * SideNavLink component with routing support
 */
const SideNavLinkWithRouting = forwardRef(
  (
    { href, pathname, search, onClick, ...rest }: SideNavLinkWithRoutingProps,
    ref: Ref<HTMLLIElement>,
  ) => {
    const { location, setLocation } = useSideNavContext();
    const to = pathname + (search ?? "");

    const handleClick = useCallback(
      (...args: Parameters<NonNullable<CoreSideNavLinkProps["onClick"]>>) => {
        args[0].preventDefault();
        history.pushState(null, "", to);
        setLocation(to);
        return onClick?.(...args);
      },
      [onClick, to, setLocation],
    );

    return (
      <CoreSideNavLink
        href={pathname != null ? to : href}
        active={pathname === location}
        onClick={pathname != null ? handleClick : onClick}
        ref={ref}
        {...rest}
      />
    );
  },
);
SideNavLinkWithRouting.displayName = CoreSideNavLink.displayName;

export type SideNavLinkProps =
  | CoreSideNavLinkProps
  | SideNavLinkWithRoutingProps;

/**
 * SideNavLink component for individual navigation links within a SideNav.
 *
 * Features:
 * - Individual navigation links with active state support
 * - Keyboard navigation integration with arrow keys
 * - Focus management and tabindex handling
 * - Accessible with proper ARIA attributes
 * - Click and keyboard event handling
 * - Disabled state support
 * - Automatic active state tracking through context
 * - Client-side routing support with pathname prop
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <SideNav.Link id="intro" href="/intro" active>
 *   Introduction
 * </SideNav.Link>
 *
 * <SideNav.Link id="intro" pathname="/intro" active>
 *   Introduction (with routing)
 * </SideNav.Link>
 */
export const SideNavLink = forwardRef(
  (rest: SideNavLinkProps, ref: Ref<HTMLLIElement>) => {
    if ("pathname" in rest) {
      return <SideNavLinkWithRouting ref={ref} {...rest} />;
    } else {
      return <CoreSideNavLink ref={ref} {...rest} />;
    }
  },
);
SideNavLink.displayName = CoreSideNavLink.displayName;
